import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiAward,
  FiCalendar,
  FiClipboard,
  FiClock,
  FiCreditCard,
  FiChevronRight,
  // FiEye,
  FiEdit2,
  FiMapPin,
  FiMessageSquare,
  FiPlus,
  FiShield,
  FiStar,
  FiTarget,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import api from '../../utils/api';
import {
  PERMISSIONS,
  canFeatureForRole,
  hasAnyPermissionForModule,
} from '../../utils/permissions';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import Pagination from '../../components/common/Pagination';
import StyledSelectDropdown from '../../components/common/StyledSelectDropdown';
import {
  isBeforeScheduledMatchStart,
  isTlFeedbackWindowOpen,
} from '../../utils/matchFeedbackSchedule';
import { formatDateTime } from '../../utils/feedbackViewFormat';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import { isDaysMatchType } from '../../utils/matchTypeUi';
import {
  getRoleIconComponent,
  TEAM_MEMBER_MODAL_ACCENT,
  TEAM_MEMBER_MODAL_HEADER_TILE_BG,
  TEAM_MEMBER_MODAL_ICON_TILE_BG,
} from '../../utils/teamMemberDetailModalUi';

const FEEDBACK_BTN_NOT_STARTED_STYLE = {
  border: '1px solid #e2e8f0',
  background: '#f1f5f9',
  color: '#94a3b8',
  cursor: 'not-allowed',
  opacity: 0.85,
};

const MATCHES_FILTER_INPUT_STYLE = {
  width: '100%',
  height: '36px',
  padding: '0 10px',
  border: '1px solid #d0d5dd',
  borderRadius: '8px',
  fontSize: '12px',
  lineHeight: 'normal',
  background: '#fff',
  boxSizing: 'border-box',
};

const MATCHES_FILTER_SELECT_STYLE = {
  ...MATCHES_FILTER_INPUT_STYLE,
};

/** Match Details modal — brand accent (icons / schedule), aligned with app gradient primary */
const MATCH_MODAL_ACCENT = '#667eea';
const MATCH_MODAL_ICON_TILE_BG = 'rgba(102, 126, 234, 0.14)';
const MATCH_MODAL_HEADER_ICON_CIRCLE_BG = 'rgba(102, 126, 234, 0.16)';
const MATCH_MODAL_SCHEDULE_PANEL_BG = 'linear-gradient(180deg, rgba(102, 126, 234, 0.09) 0%, rgba(118, 75, 162, 0.06) 100%)';

const normalizeTeamEventRows = (response) => {
  const rawRows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];

  return rawRows.map((item, index) => ({
    id: item._id || item.id || index + 1,
    matchName: item.eventName || item.event || '-',
    teamName: item.teamName || item.team || '-',
    teamLeader:
      item.teamLeaderName ||
      item.teamLeader?.name ||
      item.teamLeader?.fullName ||
      item.teamLeader ||
      '-',
    date: item.startDate || item.date || '',
    startTime: item.startTime || '',
    endDate: item.endDate || '',
    endTime: item.endTime || '',
    status: item.status || 'Not Started',
    teamMembers: Array.isArray(item.teamMembers)
      ? item.teamMembers
      : Array.isArray(item.members)
      ? item.members
      : [],
  }));
};

const formatMatchTypeLabel = (value) => {
  const text = String(value || '').trim();
  if (!text) return '-';
  return text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const toIsoDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toInputDateValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const datePartMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (datePartMatch) return datePartMatch[1];
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return toIsoDateOnly(parsed);
};

const getStartDateRangeFromKey = (rangeKey) => {
  const key = String(rangeKey || '').trim().toLowerCase();
  if (!key) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let from = null;
  let to = null;

  if (key === 'today') {
    from = today;
    to = today;
  } else if (key === 'this_week') {
    const weekStart = new Date(today);
    const day = weekStart.getDay(); // Sun=0 ... Sat=6
    const diffToMonday = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diffToMonday);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    from = weekStart;
    to = weekEnd;
  } else if (key === 'last_week') {
    const thisWeekStart = new Date(today);
    const day = thisWeekStart.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    thisWeekStart.setDate(thisWeekStart.getDate() + diffToMonday);
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
    from = lastWeekStart;
    to = lastWeekEnd;
  } else if (key === 'this_month') {
    from = new Date(today.getFullYear(), today.getMonth(), 1);
    to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (key === 'last_month') {
    from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    to = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (key === 'this_year') {
    from = new Date(today.getFullYear(), 0, 1);
    to = new Date(today.getFullYear(), 11, 31);
  }

  if (!from || !to) return null;
  return {
    from: toIsoDateOnly(from),
    to: toIsoDateOnly(to),
  };
};

const getDaysDuration = (startDateValue, endDateValue) => {
  const start = new Date(startDateValue);
  const end = new Date(endDateValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
  const oneDayMs = 24 * 60 * 60 * 1000;
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const diffDays = Math.floor((utcEnd - utcStart) / oneDayMs) + 1;
  if (!Number.isFinite(diffDays) || diffDays <= 0) return 1;
  return Math.max(1, diffDays);
};

const toPositiveIntOrNull = (value) => {
  if (typeof value === 'boolean' || value == null) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const intVal = Math.floor(num);
  return intVal > 0 ? intVal : null;
};

const hasAnyFeedbackPayload = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return false;
};

/** Event teams store `teamMembers` as `{ role: jobTypeId, member: userId }`; resolve labels for display. */
const pickUserDisplayName = (u) =>
  String(u?.name || u?.fullName || u?.userName || u?.username || u?.email || '').trim();

const resolveUserRefToLabel = (ref, usersById) => {
  if (ref == null || ref === '') return '';
  if (typeof ref === 'object' && !Array.isArray(ref)) {
    const direct = pickUserDisplayName(ref);
    if (direct) return direct;
    const id = ref._id ?? ref.id;
    if (id == null || id === '') return '';
    const u = usersById[String(id)];
    if (u) return pickUserDisplayName(u) || String(id);
    return String(id);
  }
  const key = String(ref);
  const u = usersById[key];
  if (u) return pickUserDisplayName(u) || key;
  return key;
};

const resolveRoleRefToLabel = (ref, jobTypeLabelById) => {
  if (ref == null || ref === '') return '';
  if (typeof ref === 'object' && !Array.isArray(ref)) {
    const direct = ref.name || ref.jobType || ref.title;
    if (direct) return String(direct);
    const id = ref._id ?? ref.id;
    if (id != null && id !== '') {
      const label = jobTypeLabelById[String(id)];
      if (label) return label;
      return String(id);
    }
    return '';
  }
  const key = String(ref);
  if (jobTypeLabelById[key]) return jobTypeLabelById[key];
  return key;
};

const resolveAssignedTeamLeaderLabel = (row, usersById) => {
  const v = row?.teamLeader;
  if (v == null || v === '' || v === '-') return '-';
  const resolved = resolveUserRefToLabel(v, usersById);
  return resolved && resolved !== '[object Object]' ? resolved : '-';
};

const resolveTeamMemberCells = (member, usersById, jobTypeLabelById) => {
  const roleLabel =
    resolveRoleRefToLabel(member?.role, jobTypeLabelById) ||
    resolveRoleRefToLabel(member?.roleId, jobTypeLabelById) ||
    (member?.roleName ? String(member.roleName) : '') ||
    '-';
  const memberLabel =
    resolveUserRefToLabel(member?.member, usersById) ||
    resolveUserRefToLabel(member?.employee, usersById) ||
    resolveUserRefToLabel(member?.user, usersById) ||
    pickUserDisplayName(member) ||
    '-';
  return { roleLabel, memberLabel };
};

let lastMatchesFetchKey = '';
let lastMatchesFetchAt = 0;

/** Same pattern as TeamsEventsPage — keys map to GET /api/matches sortBy. */
const MATCH_TABLE_COLUMN_COUNT = 11;

const SORTABLE_MATCH_COLUMNS = [
  'association_id',
  'season',
  'tournament_name',
  'matchName',
  'matchType',
  'venue',
  'teamName',
  'startDate',
  'endDate',
];

const MATCH_SORT_LEFT_ALIGNED_KEYS = [
  'association_id',
  'season',
  'tournament_name',
  'matchName',
  'matchType',
  'venue',
  'teamName',
];

const MATCHES_DEFAULT_FILTERS = {
  associationId: '',
  matchName: '',
  matchType: '',
  venue: '',
  status: '',
  teamName: '',
  startDate: '',
  customStartDate: '',
  customEndDate: '',
  season: '',
  tournament: '',
};

const Matches = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobTypeRows, loading: referenceDataLoading } = useReferenceData();
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const normalizedUserRole = String(currentUser?.role || '').trim().toLowerCase();
  const isEmployeeUser = normalizedUserRole === 'employee';
  const isTeamScopedMatchesUser =
    isEmployeeUser ||
    normalizedUserRole === 'team-leader' ||
    normalizedUserRole === 'teamleader';
  const appBasePath = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const matchesFiltersStorageKey = `${appBasePath}:matches:filters`;
  const currentUserIds = useMemo(
    () =>
      new Set(
        [
          currentUser?.id,
          currentUser?._id,
          currentUser?.userId,
          currentUser?.employeeId,
          currentUser?.employee?._id,
          currentUser?.employee?.id,
        ]
          .filter(Boolean)
          .map((value) => String(value))
      ),
    [currentUser]
  );
  const currentUserMatchFilterId = useMemo(
    () => String(currentUser?.id || currentUser?._id || '').trim(),
    [currentUser?.id, currentUser?._id]
  );
  const currentUserNames = useMemo(
    () =>
      new Set(
        [
          currentUser?.name,
          currentUser?.fullName,
          currentUser?.userName,
          currentUser?.username,
          currentUser?.email,
        ]
          .filter(Boolean)
          .map((value) => String(value).trim().toLowerCase())
      ),
    [currentUser]
  );
  const canViewMatches = useMemo(
    () => hasAnyPermissionForModule(normalizedUserRole, rolePermissions, 'Matches'),
    [normalizedUserRole, rolePermissions]
  );
  const canAddMatch = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.ADD_MATCH]),
    [normalizedUserRole, rolePermissions]
  );
  const canEditMatchPerm = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.EDIT_MATCH]),
    [normalizedUserRole, rolePermissions]
  );
  const canGiveMatchTlFeedback = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.GIVE_MATCH_TL_FEEDBACK]),
    [normalizedUserRole, rolePermissions]
  );
  const canViewMatchTlFeedback = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.VIEW_MATCH_TL_FEEDBACK]),
    [normalizedUserRole, rolePermissions]
  );
  const canGiveSupervisorFeedback = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.GIVE_SUPERVISOR_FEEDBACK]),
    [normalizedUserRole, rolePermissions]
  );
  const canViewSupervisorFeedback = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.VIEW_SUPERVISOR_FEEDBACK]),
    [normalizedUserRole, rolePermissions]
  );
  const hasShowAllMatchesPermission = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.SHOW_ALL_MATCHES,
        'show_all_matches',
      ]),
    [normalizedUserRole, rolePermissions]
  );
  /** When true, send memberId and narrow each row to the user's team (unless Show All Matches is granted). */
  const narrowMatchesToCurrentUserTeams =
    isTeamScopedMatchesUser && !hasShowAllMatchesPermission;
  const [teamEventRows, setTeamEventRows] = useState([]);
  const [matchRows, setMatchRows] = useState([]);
  const matchRowsRef = useRef([]);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [totalMatches, setTotalMatches] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedAssignedTeam, setSelectedAssignedTeam] = useState(null);
  const [isAddMatchOpen, setIsAddMatchOpen] = useState(false);
  const [editingMatchId, setEditingMatchId] = useState('');
  const [isTimeOnlyEdit, setIsTimeOnlyEdit] = useState(false);
  const [addMatchSubmitting, setAddMatchSubmitting] = useState(false);
  const [addMatchForm, setAddMatchForm] = useState({
    associationId: '',
    season: '',
    tournamentName: '',
    matchName: '',
    matchType: '',
    matchLocation: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
  });
  const [filters, setFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem(matchesFiltersStorageKey);
      if (!raw) return MATCHES_DEFAULT_FILTERS;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return MATCHES_DEFAULT_FILTERS;
      return { ...MATCHES_DEFAULT_FILTERS, ...parsed };
    } catch (error) {
      return MATCHES_DEFAULT_FILTERS;
    }
  });
  const [debouncedTextFilters, setDebouncedTextFilters] = useState(() => ({
    associationId: filters.associationId,
    matchName: filters.matchName,
    venue: filters.venue,
    teamName: filters.teamName,
    season: filters.season,
    tournament: filters.tournament,
  }));
  const [sortConfig, setSortConfig] = useState({
    key: 'startDate',
    direction: 'desc',
  });
  const [directoryUsers, setDirectoryUsers] = useState([]);

  const usersById = useMemo(() => {
    const map = {};
    directoryUsers.forEach((u) => {
      const key = String(u.id || u._id || '');
      if (key) map[key] = u;
    });
    return map;
  }, [directoryUsers]);

  const jobTypeLabelById = useMemo(() => {
    const map = {};
    jobTypeRows.forEach((item) => {
      if (typeof item === 'string') return;
      const id = String(item?._id || item?.id || '');
      const name = String(item?.name || item?.jobType || item?.title || '').trim();
      if (id && name) map[id] = name;
    });
    return map;
  }, [jobTypeRows]);

  useEffect(() => {
    let cancelled = false;
    const loadDirectory = async () => {
      try {
        const usersResponse = await api.getUsers({ isActive: 'true', limit: 1000 });
        if (cancelled) return;
        const userRows = Array.isArray(usersResponse?.data)
          ? usersResponse.data
          : Array.isArray(usersResponse?.data?.users)
          ? usersResponse.data.users
          : Array.isArray(usersResponse?.users)
          ? usersResponse.users
          : [];
        setDirectoryUsers(userRows);
      } catch (error) {
        if (!cancelled) console.error('Matches page: failed to load user/job-type directory', error);
      }
    };
    loadDirectory();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    matchRowsRef.current = matchRows;
  }, [matchRows]);

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(matchesFiltersStorageKey, JSON.stringify(filters));
    } catch (error) {
      // Ignore storage write failures (private mode/quota/security settings).
    }
  }, [filters, matchesFiltersStorageKey]);

  useEffect(() => {
    return () => {
      const nextPath = String(window.location.pathname || '').toLowerCase();
      const isFeedbackRoute =
        nextPath.includes('/feedback') ||
        nextPath.includes('/view-feedback') ||
        nextPath.includes('/supervisor-feedback') ||
        nextPath.includes('/view-supervisor-feedback');
      if (isFeedbackRoute) return;
      try {
        sessionStorage.removeItem(matchesFiltersStorageKey);
      } catch (error) {
        // Ignore storage delete failures.
      }
    };
  }, [matchesFiltersStorageKey]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedTextFilters((prev) => {
        const next = {
          associationId: filters.associationId,
          matchName: filters.matchName,
          venue: filters.venue,
          teamName: filters.teamName,
          season: filters.season,
          tournament: filters.tournament,
        };
        const isSame =
          prev.associationId === next.associationId &&
          prev.matchName === next.matchName &&
          prev.venue === next.venue &&
          prev.teamName === next.teamName &&
          prev.season === next.season &&
          prev.tournament === next.tournament;
        return isSame ? prev : next;
      });
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [
    filters.associationId,
    filters.matchName,
    filters.venue,
    filters.teamName,
    filters.season,
    filters.tournament,
  ]);

  const loadMatches = useCallback(
    async (page = currentPage, limit = itemsPerPage) => {
      try {
        const reuseVisibleTable = matchRowsRef.current.length > 0;
        if (reuseVisibleTable) {
          setTableRefreshing(true);
        } else {
          setLoading(true);
        }
        const submissionStatusValue =
          filters.status === 'completed'
            ? 'completed'
            : filters.status === 'pending'
            ? 'pending'
            : '';
        const queryParams = {
          page,
          limit,
        };
        const associationId = debouncedTextFilters.associationId.trim();
        const matchName = debouncedTextFilters.matchName.trim();
        const matchType = filters.matchType.trim();
        const venue = debouncedTextFilters.venue.trim();
        const teamName = debouncedTextFilters.teamName.trim();
        const startDate = filters.startDate;
        const customStartDate = String(filters.customStartDate || '').trim();
        const customEndDate = String(filters.customEndDate || '').trim();
        const season = debouncedTextFilters.season.trim();
        const tournament = debouncedTextFilters.tournament.trim();

        if (associationId) queryParams.association_id = associationId;
        if (matchName) queryParams.matchName = matchName;
        if (matchType) queryParams.matchType = matchType;
        if (venue) {
          queryParams.venue = venue;
          queryParams.matchLocation = venue;
        }
        if (submissionStatusValue) {
          queryParams.submissionStatus = submissionStatusValue;
          queryParams.feedbackStatus = submissionStatusValue;
        }
        if (teamName) queryParams.teamName = teamName;
        if (startDate) {
          queryParams.startDate = startDate;
          queryParams.dateRange = startDate;
          if (startDate === 'custom') {
            if (customStartDate) queryParams.startDateFrom = customStartDate;
            if (customEndDate) queryParams.startDateTo = customEndDate;
          } else {
            const computedRange = getStartDateRangeFromKey(startDate);
            if (computedRange) {
              queryParams.startDateFrom = computedRange.from;
              queryParams.startDateTo = computedRange.to;
            }
          }
        }
        if (season) queryParams.season = season;
        if (tournament) {
          queryParams.tournament_name = tournament;
          queryParams.tournament = tournament;
        }

        queryParams.sortBy = sortConfig.key;
        queryParams.sortOrder = sortConfig.direction;

        const requestKey = JSON.stringify({
          page,
          limit,
          ...queryParams,
        });
        const now = Date.now();
        if (
          requestKey === lastMatchesFetchKey &&
          now - lastMatchesFetchAt < 800
        ) {
          setLoading(false);
          setTableRefreshing(false);
          return;
        }
        lastMatchesFetchKey = requestKey;
        lastMatchesFetchAt = now;

        if (narrowMatchesToCurrentUserTeams) {
          queryParams.memberId = currentUserMatchFilterId;
        }
        const [matchesResponse, teamsResponse] = await Promise.all([
          api.getMatches(queryParams),
          api.getEventTeams({ page: 1, limit: 1000 }),
        ]);

        const matchesPayload = matchesResponse?.data ?? matchesResponse;
        const rawMatches = Array.isArray(matchesPayload)
          ? matchesPayload
          : Array.isArray(matchesPayload?.data)
          ? matchesPayload.data
          : [];

        const builtRows = rawMatches.map((item, index) => {
            const eventTeams = [
              ...(item?.eventTeam ? [item.eventTeam] : []),
              ...(Array.isArray(item?.eventTeams) ? item.eventTeams : []),
            ].filter(Boolean);
            const resolveTeamId = (teamRef) =>
              String(teamRef?._id || teamRef?.id || '').trim();
            const resolveTeamLeaderId = (teamRef) =>
              String(
                teamRef?.teamLeaderId ||
                  teamRef?.teamLeader?._id ||
                  teamRef?.teamLeader?.id ||
                  ''
              ).trim();
            const resolveTeamLeaderName = (teamRef) =>
              String(
                teamRef?.teamLeaderName ||
                  teamRef?.teamLeader?.name ||
                  teamRef?.teamLeader?.fullName ||
                  teamRef?.teamLeader ||
                  ''
              )
                .trim()
                .toLowerCase();
            const resolveMemberIds = (teamRef) =>
              (Array.isArray(teamRef?.teamMembers) ? teamRef.teamMembers : [])
                .flatMap((member) =>
                  [
                    member?.member?._id,
                    member?.member?.id,
                    member?.member,
                    member?.employee?._id,
                    member?.employee?.id,
                    member?.employee,
                    member?.user?._id,
                    member?.user?.id,
                    member?.user,
                    member?.userId,
                    member?.memberId,
                    member?.id,
                  ]
                    .filter(Boolean)
                    .map((value) => String(value))
                );
            const resolveMemberNames = (teamRef) =>
              (Array.isArray(teamRef?.teamMembers) ? teamRef.teamMembers : [])
                .map((member) => pickUserDisplayName(member?.member || member?.employee || member?.user || member).trim().toLowerCase())
                .filter(Boolean);
            const userTeam = eventTeams.find((teamRef) => {
              const teamLeaderId = resolveTeamLeaderId(teamRef);
              if (teamLeaderId && currentUserIds.has(teamLeaderId)) return true;
              const teamLeaderName = resolveTeamLeaderName(teamRef);
              if (teamLeaderName && currentUserNames.has(teamLeaderName)) return true;
              const memberIds = resolveMemberIds(teamRef);
              if (memberIds.some((id) => currentUserIds.has(id))) return true;
              const memberNames = resolveMemberNames(teamRef);
              return memberNames.some((name) => currentUserNames.has(name));
            }) || {};
            const fallbackTeam = eventTeams[0] || {};
            const team = narrowMatchesToCurrentUserTeams
              ? (item?.eventTeam && typeof item.eventTeam === 'object' ? item.eventTeam : userTeam)
              : fallbackTeam;
            const teamId = resolveTeamId(team);
            const teamLeaderId = resolveTeamLeaderId(team);
            const teamLeaderName = resolveTeamLeaderName(team);
            const isRowLeaderForCurrentUser =
              Boolean(item?.isCurrentUserTeamLeaderForMatch) ||
              (teamLeaderId && currentUserIds.has(teamLeaderId)) ||
              (teamLeaderName && currentUserNames.has(teamLeaderName));
            const isDaysMatch = isDaysMatchType(item.matchType);
            const requiredDaysFromPayload = toPositiveIntOrNull(
              item.totalDays ??
                item.matchDays ??
                item.durationDays ??
                item.numberOfDays ??
                item.daysCount ??
                item.noOfDays
            );
            const requiredDays =
              requiredDaysFromPayload ??
              getDaysDuration(item.startDate, item.endDate);
            const submittedDayArrayCandidates = [
              item.submittedDays,
              item.feedbackDays,
              item.matchFeedbackDays,
              item.submittedFeedbackDaysList,
              item.daysSubmitted,
            ].filter(Array.isArray);
            const submittedDaysFromArray = submittedDayArrayCandidates.length
              ? Math.max(...submittedDayArrayCandidates.map((arr) => arr.length))
              : 0;
            const submittedDaysCountRaw = toPositiveIntOrNull(
              item.submittedDaysCount ??
                item.feedbackDaysSubmitted ??
                item.matchFeedbackDaysSubmitted ??
                item.submittedFeedbackDays ??
                submittedDaysFromArray
            );
            const hasSubmittedDaysCount = submittedDaysCountRaw != null;
            const allDaysSubmittedByCount =
              hasSubmittedDaysCount && submittedDaysCountRaw >= requiredDays;
            const allDaysSubmittedByFlag = Boolean(
              item.allDaysFeedbackSubmitted ??
                item.allDaysMatchFeedbackSubmitted ??
                item.matchFeedbackAllDaysSubmitted
            );
            const normalizedMatchFeedbackSubmitted = isDaysMatch
              ? (allDaysSubmittedByFlag || allDaysSubmittedByCount)
              : Boolean(item.matchFeedbackSubmitted);
            const feedbackStatusFromApi = String(
              item.matchFeedbackStatus || item.feedbackStatus || item.status || ''
            ).toLowerCase();
            const isMatchCompletedByApi =
              feedbackStatusFromApi === 'completed' ||
              feedbackStatusFromApi === 'complete' ||
              feedbackStatusFromApi === 'submitted' ||
              feedbackStatusFromApi === 'done';
            const isPartialByApi =
              feedbackStatusFromApi.includes('partial') ||
              feedbackStatusFromApi.includes('progress') ||
              feedbackStatusFromApi.includes('pending') ||
              feedbackStatusFromApi.includes('draft') ||
              feedbackStatusFromApi.includes('incomplete') ||
              feedbackStatusFromApi.includes('started');
            const hasMatchFeedbackProgressBySignal = Boolean(
              item.matchFeedbackStarted ||
                item.isMatchFeedbackStarted ||
                item.matchFeedbackDraftSaved ||
                item.matchFeedbackInProgress ||
                item.hasMatchFeedback ||
                item.matchFeedbackExists
            ) || (
              toPositiveIntOrNull(
                item.matchFeedbackCount ??
                  item.feedbackCount ??
                  item.submittedFeedbackCount ??
                  item.matchFeedbackSubmittedCount ??
                  item.feedbackFilledCount
              ) != null
            ) || (
              [
                item.matchFeedback,
                item.feedback,
                item.matchFeedbackEntries,
                item.feedbackEntries,
              ].some((val) => Array.isArray(val) && val.length > 0)
            );
            const normalizedMatchFeedbackStatus = (normalizedMatchFeedbackSubmitted || isMatchCompletedByApi)
              ? 'completed'
              : ((isDaysMatch && hasSubmittedDaysCount && submittedDaysCountRaw > 0) || isPartialByApi || hasMatchFeedbackProgressBySignal)
              ? 'partial'
              : 'none';
            const supervisorStatusFromApi = String(
              item.supervisorFeedbackStatus ||
                item.supervisorStatus ||
                item.feedbackStatus ||
                item.status ||
                ''
            ).toLowerCase();
            const supervisorFeedbackSubmitted = Boolean(item.supervisorFeedbackSubmitted);
            const supervisorSubmittedDayArrayCandidates = [
              item.supervisorSubmittedDays,
              item.supervisorFeedbackDays,
              item.supervisorFeedbackSubmittedDays,
              item.supervisorSubmittedFeedbackDaysList,
            ].filter(Array.isArray);
            const supervisorSubmittedDaysFromArray = supervisorSubmittedDayArrayCandidates.length
              ? Math.max(...supervisorSubmittedDayArrayCandidates.map((arr) => arr.length))
              : 0;
            const supervisorSubmittedDaysCountRaw = toPositiveIntOrNull(
              item.supervisorSubmittedDaysCount ??
                item.supervisorFeedbackDaysSubmitted ??
                item.supervisorSubmittedFeedbackDays ??
                supervisorSubmittedDaysFromArray
            );
            const hasSupervisorSubmittedDaysCount = supervisorSubmittedDaysCountRaw != null;
            const supervisorAllDaysSubmittedByCount =
              hasSupervisorSubmittedDaysCount && supervisorSubmittedDaysCountRaw >= requiredDays;
            const supervisorAllDaysSubmittedByFlag = Boolean(
              item.supervisorAllDaysFeedbackSubmitted ??
                item.allDaysSupervisorFeedbackSubmitted ??
                item.supervisorFeedbackAllDaysSubmitted
            );
            const supervisorCompletedByApi =
              supervisorStatusFromApi === 'completed' ||
              supervisorStatusFromApi === 'complete' ||
              supervisorStatusFromApi === 'submitted' ||
              supervisorStatusFromApi === 'done';
            const supervisorFeedbackPartial = !supervisorFeedbackSubmitted && (
              supervisorStatusFromApi.includes('partial') ||
              supervisorStatusFromApi.includes('progress') ||
              supervisorStatusFromApi.includes('pending') ||
              supervisorStatusFromApi.includes('draft') ||
              supervisorStatusFromApi.includes('incomplete') ||
              supervisorStatusFromApi.includes('started') ||
              Boolean(
                item.supervisorFeedbackStarted ||
                  item.supervisorFeedbackInProgress ||
                  item.supervisorFeedbackDraftSaved ||
                  item.hasSupervisorFeedback ||
                  item.supervisorFeedbackExists
              ) ||
              (
                toPositiveIntOrNull(
                  item.supervisorFeedbackCount ??
                    item.supervisorSubmittedFeedbackCount ??
                    item.supervisorFeedbackFilledCount
                ) != null
              ) ||
              (
                [
                  item.supervisorFeedback,
                  item.supervisorFeedbackEntries,
                  item.supervisorResponses,
                  item.supervisorFeedbackData,
                  item.supervisorFeedbackForm,
                  item.supervisorFeedbackDraft,
                ].some((val) => hasAnyFeedbackPayload(val))
              )
            );
            const normalizedSupervisorFeedbackSubmitted = supervisorFeedbackSubmitted || supervisorCompletedByApi || (
              isDaysMatch && (supervisorAllDaysSubmittedByFlag || supervisorAllDaysSubmittedByCount)
            );
            const normalizedSupervisorFeedbackStatus = normalizedSupervisorFeedbackSubmitted
              ? 'completed'
              : (supervisorFeedbackPartial || (
                isDaysMatch &&
                hasSupervisorSubmittedDaysCount &&
                supervisorSubmittedDaysCountRaw > 0
              ))
              ? 'partial'
              : 'none';
            const teamChangeLocked =
              Boolean(item.matchFeedbackSubmitted) ||
              Boolean(item.supervisorFeedbackSubmitted) ||
              normalizedMatchFeedbackSubmitted ||
              normalizedSupervisorFeedbackSubmitted ||
              (isDaysMatch && hasSubmittedDaysCount && submittedDaysCountRaw > 0) ||
              (isDaysMatch &&
                hasSupervisorSubmittedDaysCount &&
                supervisorSubmittedDaysCountRaw > 0) ||
              isMatchCompletedByApi ||
              supervisorCompletedByApi;
            return {
              id: item._id || item.id || index + 1,
              associationId: item.association_id || '-',
              season: item.season || '-',
              tournamentName: item.tournament_name || '-',
              matchName: item.matchName || '-',
              matchType: item.matchType || '',
              matchLocation: item.matchLocation || '-',
              date: item.startDate || '',
              startTime: item.startTime || '',
              endDate: item.endDate || '',
              endTime: item.endTime || '',
              // event team fields
              eventTeamId: teamId,
              eventName: team.eventName || '-',
              teamName: team.teamName || '-',
              teamLeader:
                team.teamLeaderName ||
                team.teamLeader?.name ||
                team.teamLeader?.fullName ||
                team.teamLeader ||
                '-',
              teamLeaderId,
              teamStatus: team.status || '-',
              teamMembers: Array.isArray(team.teamMembers) ? team.teamMembers : [],
              isCurrentUserTeamLeaderForMatch: isRowLeaderForCurrentUser,
              avgDiscipline: item.avgDiscipline,
              avgTechAccuracy: item.avgTechAccuracy,
              // feedback status flags from API
              matchFeedbackSubmitted: normalizedMatchFeedbackSubmitted,
              matchFeedbackStatus: normalizedMatchFeedbackStatus,
              supervisorFeedbackSubmitted: normalizedSupervisorFeedbackSubmitted,
              supervisorFeedbackStatus: normalizedSupervisorFeedbackStatus,
              teamChangeLocked,
            };
          });
        const visibleRows = builtRows;

        const resolvedTotal = Number(
          matchesResponse?.total ??
            matchesPayload?.total ??
            matchesResponse?.count ??
            matchesPayload?.count ??
            matchesResponse?.pagination?.total ??
            matchesPayload?.pagination?.total ??
            rawMatches.length
        );
        const normalizedTotal =
          Number.isFinite(resolvedTotal) && resolvedTotal >= 0
            ? resolvedTotal
            : rawMatches.length;
        const resolvedTotalPages = Number(
          matchesResponse?.totalPages ??
            matchesPayload?.totalPages ??
            matchesResponse?.pagination?.totalPages ??
            matchesPayload?.pagination?.totalPages ??
            Math.ceil(normalizedTotal / Math.max(1, Number(limit) || 1))
        );

        setMatchRows(visibleRows);
        setTotalMatches(narrowMatchesToCurrentUserTeams ? visibleRows.length : normalizedTotal);
        setTotalPages(
          narrowMatchesToCurrentUserTeams
            ? Math.max(1, Math.ceil(visibleRows.length / Math.max(1, Number(limit) || 1)))
            : Math.max(1, Number.isFinite(resolvedTotalPages) ? resolvedTotalPages : 1)
        );
        setTeamEventRows(normalizeTeamEventRows(teamsResponse));
      } catch (error) {
        toast.error(error.message || 'Unable to load matches');
      } finally {
        setLoading(false);
        setTableRefreshing(false);
      }
    },
    [
      currentPage,
      debouncedTextFilters,
      filters.matchType,
      filters.status,
      filters.startDate,
      filters.customStartDate,
      filters.customEndDate,
      itemsPerPage,
      sortConfig.key,
      sortConfig.direction,
      narrowMatchesToCurrentUserTeams,
      currentUserMatchFilterId,
      currentUserIds,
      currentUserNames,
    ]
  );

  /** TL feedback submit: assigned TL for the match, or anyone with both give TL + edit match (from User Roles only). */
  const canGiveTlFeedbackForMatchRow = useCallback(
    (row) => {
      if (!canGiveMatchTlFeedback) return false;
      if (row?.isCurrentUserTeamLeaderForMatch) return true;
      if (canEditMatchPerm) return true;
      return false;
    },
    [canGiveMatchTlFeedback, canEditMatchPerm]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!canViewMatches) {
      setLoading(false);
      setTableRefreshing(false);
      setMatchRows([]);
      setTotalMatches(0);
      setTotalPages(1);
      return;
    }
    loadMatches(currentPage, itemsPerPage);
  }, [loadMatches, currentPage, itemsPerPage, permissionsReady, canViewMatches]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleAddMatchFieldChange = (event) => {
    const { name, value } = event.target;
    setAddMatchForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => {
      if (prev[name] === value) return prev;
      if (name === 'startDate') {
        return {
          ...prev,
          startDate: value,
          customStartDate: value === 'custom' ? prev.customStartDate : '',
          customEndDate: value === 'custom' ? prev.customEndDate : '',
        };
      }
      if (name === 'customStartDate') {
        return {
          ...prev,
          customStartDate: value,
          customEndDate: prev.customEndDate && prev.customEndDate < value ? value : prev.customEndDate,
        };
      }
      return { ...prev, [name]: value };
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters(MATCHES_DEFAULT_FILTERS);
    setSortConfig({ key: 'startDate', direction: 'desc' });
    setCurrentPage(1);
  };

  const closeAddMatchModal = () => {
    setIsAddMatchOpen(false);
    setEditingMatchId('');
    setIsTimeOnlyEdit(false);
    setAddMatchForm({
      associationId: '',
      season: '',
      tournamentName: '',
      matchName: '',
      matchType: '',
      matchLocation: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
    });
  };

  const openAddMatchModal = () => {
    if (!canAddMatch) {
      toast.error('You do not have permission to add matches');
      return;
    }
    setEditingMatchId('');
    setIsTimeOnlyEdit(false);
    setAddMatchForm({
      associationId: '',
      season: '',
      tournamentName: '',
      matchName: '',
      matchType: '',
      matchLocation: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
    });
    setIsAddMatchOpen(true);
  };

  const openEditMatchModal = (row) => {
    if (!canEditMatchPerm) {
      toast.error('You do not have permission to edit matches');
      return;
    }
    const hasNotStarted = isBeforeScheduledMatchStart({
      startDate: row?.date,
      startTime: row?.startTime,
    });

    const matchId = String(row?.id || '').trim();
    if (!matchId) {
      toast.error('Invalid match selected for editing');
      return;
    }
    setEditingMatchId(matchId);
    setIsTimeOnlyEdit(!hasNotStarted);
    setAddMatchForm({
      associationId: String(row?.associationId === '-' ? '' : row?.associationId || ''),
      season: String(row?.season === '-' ? '' : row?.season || ''),
      tournamentName: String(row?.tournamentName === '-' ? '' : row?.tournamentName || ''),
      matchName: String(row?.matchName === '-' ? '' : row?.matchName || ''),
      matchType: String(row?.matchType || ''),
      matchLocation: String(row?.matchLocation === '-' ? '' : row?.matchLocation || ''),
      startDate: toInputDateValue(row?.date),
      startTime: String(row?.startTime || ''),
      endDate: toInputDateValue(row?.endDate),
      endTime: String(row?.endTime || ''),
    });
    setIsAddMatchOpen(true);
  };

  const handleAddMatchSubmit = async (event) => {
    event.preventDefault();
    const isEditing = Boolean(editingMatchId);

    if (isEditing && !canEditMatchPerm) {
      toast.error('You do not have permission to edit matches');
      return;
    }
    if (!isEditing && !canAddMatch) {
      toast.error('You do not have permission to add matches');
      return;
    }

    if (!isEditing || !isTimeOnlyEdit) {
      if (
        !addMatchForm.associationId.trim() ||
        !addMatchForm.season.trim() ||
        !addMatchForm.tournamentName.trim() ||
        !addMatchForm.matchName.trim() ||
        !addMatchForm.matchLocation.trim() ||
        !addMatchForm.startDate ||
        !addMatchForm.startTime ||
        !addMatchForm.endDate ||
        !addMatchForm.endTime
      ) {
        toast.error('Please fill all required fields');
        return;
      }
    } else if (
      !addMatchForm.startDate ||
      !addMatchForm.startTime ||
      !addMatchForm.endDate ||
      !addMatchForm.endTime
    ) {
      toast.error('Please fill start/end date and time');
      return;
    }

    if (addMatchForm.endDate < addMatchForm.startDate) {
      toast.error('End date cannot be earlier than start date');
      return;
    }

    if (
      addMatchForm.endDate === addMatchForm.startDate &&
      addMatchForm.endTime <= addMatchForm.startTime
    ) {
      toast.error('End time must be after start time on the same day');
      return;
    }

    // Combine date + time into the API's wire-format "YYYY-MM-DD HH:mm:ss".
    // <input type="time"> always returns 24-hour "HH:mm", so we append ":00".
    const matchStartDateCombined = `${addMatchForm.startDate} ${addMatchForm.startTime}:00`;
    const matchEndDateCombined = `${addMatchForm.endDate} ${addMatchForm.endTime}:00`;

    try {
      setAddMatchSubmitting(true);
      const payload = isEditing && isTimeOnlyEdit
        ? {
            match_start_date: matchStartDateCombined,
            match_end_date: matchEndDateCombined,
          }
        : {
            association_id: addMatchForm.associationId.trim(),
            season: addMatchForm.season.trim(),
            tournament_name: addMatchForm.tournamentName.trim(),
            matchName: addMatchForm.matchName.trim(),
            matchType: addMatchForm.matchType.trim(),
            matchLocation: addMatchForm.matchLocation.trim(),
            match_start_date: matchStartDateCombined,
            match_end_date: matchEndDateCombined,
          };

      if (editingMatchId) {
        await api.updateMatch(editingMatchId, payload);
        toast.success('Match updated successfully');
      } else {
        await api.createMatch(payload);
        toast.success('Match added successfully');
      }
      closeAddMatchModal();
      setCurrentPage(1);
      await loadMatches(1, itemsPerPage);
    } catch (error) {
      toast.error(
        error.message || (editingMatchId ? 'Failed to update match' : 'Failed to add match')
      );
    } finally {
      setAddMatchSubmitting(false);
    }
  };

  const handleAssignTeam = async (row, eventTeamId) => {
    if (!canEditMatchPerm) {
      toast.error('You do not have permission to assign teams to matches');
      return;
    }
    const normalizedEventTeamId = String(eventTeamId || '').trim();
    const currentEventTeamId = String(row.eventTeamId || '').trim();

    // Avoid firing update API when selection has not changed.
    if (normalizedEventTeamId === currentEventTeamId) return;

    if (row.teamChangeLocked) {
      toast.error('Cannot change team after feedback has been submitted');
      return;
    }

    try {
      const payload = normalizedEventTeamId
        ? { eventTeam: normalizedEventTeamId }
        : { eventTeam: null };
      await api.updateMatch(String(row.id), payload);
      const selectedTeam = teamEventRows.find(
        (t) => String(t.id) === normalizedEventTeamId
      );
      setMatchRows((prev) =>
        prev.map((m) =>
          String(m.id) === String(row.id)
            ? {
                ...m,
                eventTeamId: normalizedEventTeamId || '',
                eventName: selectedTeam?.matchName || '-',
                teamName: selectedTeam?.teamName || '-',
                teamLeader:
                  selectedTeam?.teamLeaderName ||
                  selectedTeam?.teamLeader?.name ||
                  selectedTeam?.teamLeader?.fullName ||
                  selectedTeam?.teamLeader ||
                  '-',
                teamMembers: selectedTeam?.teamMembers || [],
              }
            : m
        )
      );
      toast.success(
        normalizedEventTeamId ? 'Team assigned successfully' : 'Team unassigned'
      );
    } catch (err) {
      toast.error(err.message || 'Failed to assign team');
    }
  };

  const openFeedbackPage = (row) => {
    if (!canGiveMatchTlFeedback) {
      toast.error('You do not have permission to give TL feedback');
      return;
    }
    if (!row.eventTeamId) {
      toast.error('No event team assigned to this match');
      return;
    }
    const rowIsDaysMatch = isDaysMatchType(row.matchType);
    if (
      !rowIsDaysMatch &&
      !isTlFeedbackWindowOpen({
        isDaysMatch: false,
        startDate: row.date,
        startTime: row.startTime,
      })
    ) {
      toast.error(
        'TL feedback is available only after the match starts (scheduled start date and time).'
      );
      return;
    }
    const routePath = isEmployeeUser
      ? `${appBasePath}/teams-events/${row.eventTeamId}/feedback`
      : `${appBasePath}/matches/${row.id}/feedback/${row.eventTeamId}`;
    navigate(routePath, {
      state: {
        from: isEmployeeUser ? `${appBasePath}/teams-events` : `${appBasePath}/matches`,
        matchId: row.id,
        matchName: row.matchName || row.name || '',
        matchLocation: row.matchLocation || '',
        matchType: row.matchType || '',
        startDate: row.date || '',
        startTime: row.startTime || '',
        endDate: row.endDate || '',
        endTime: row.endTime || '',
      },
    });
  };

  const openViewFeedback = (row) => {
    if (!canViewMatchTlFeedback) {
      toast.error('You do not have permission to view TL feedback');
      return;
    }
    const routePath = `${appBasePath}/matches/${row.id}/view-feedback`;
    navigate(routePath, {
      state: {
        from: `${appBasePath}/matches`,
        matchType: row.matchType || '',
        eventTeamId: row.eventTeamId || '',
      },
    });
  };

  const handleSupervisorFeedbackClick = async (row) => {
    const supervisorOpensView = Boolean(row.supervisorFeedbackSubmitted);
    if (supervisorOpensView && !canViewSupervisorFeedback) {
      toast.error('You do not have permission to view supervisor feedback');
      return;
    }
    if (!supervisorOpensView && !canGiveSupervisorFeedback) {
      toast.error('You do not have permission to give supervisor feedback');
      return;
    }
    if (!row.eventTeamId) {
      toast.error('No event team assigned to this match');
      return;
    }
    const targetPath = row.supervisorFeedbackSubmitted
      ? `${appBasePath}/matches/${row.id}/view-supervisor-feedback`
      : `${appBasePath}/matches/${row.id}/supervisor-feedback/${row.eventTeamId}`;
    if (
      !row.supervisorFeedbackSubmitted &&
      !isDaysMatchType(row.matchType) &&
      !isTlFeedbackWindowOpen({
        isDaysMatch: false,
        startDate: row.date,
        startTime: row.startTime,
      })
    ) {
      toast.error(
        'Supervisor feedback is available only after the match starts (scheduled start date and time).'
      );
      return;
    }
    navigate(targetPath, {
      state: {
        from: `${appBasePath}/matches`,
        matchType: row.matchType || '',
        startDate: row.date || '',
        startTime: row.startTime || '',
        endDate: row.endDate || '',
        endTime: row.endTime || '',
        eventTeamId: row.eventTeamId || '',
      },
    });
  };

  const matchTypeOptions = useMemo(() => ['T20', 'One-Day', 'Days'], []);

  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = matchRows;

  const handleSort = (key) => {
    if (!SORTABLE_MATCH_COLUMNS.includes(key)) return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderSortableHeader = (key, label, title, extraThStyle = {}) => {
    const isActive = sortConfig.key === key;
    const textAlign = MATCH_SORT_LEFT_ALIGNED_KEYS.includes(key) ? 'left' : 'center';
    return (
      <th
        key={key}
        scope="col"
        style={{
          textAlign,
          padding: '8px 10px',
          color: isActive ? '#93c5fd' : '#f8fafc',
          fontSize: '12px',
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          ...extraThStyle,
        }}
        onClick={() => handleSort(key)}
        title={title}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            justifyContent: textAlign === 'center' ? 'center' : 'flex-start',
            width: '100%',
          }}
        >
          <span>{label}</span>
          <span style={{ color: isActive ? '#93c5fd' : '#cbd5e1', fontSize: '10px' }}>
            {getSortIcon(key)}
          </span>
        </span>
      </th>
    );
  };

  if (!permissionsReady || referenceDataLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
        Loading…
      </div>
    );
  }

  if (!canViewMatches) {
    return <Navigate to={`${appBasePath}/dashboard`} replace />;
  }

  return (
    <div style={{ padding: 0, maxWidth: '100%' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
          padding: '16px',
        }}
      >
        {canAddMatch && (
          <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn-action-primary"
              onClick={openAddMatchModal}
              style={{
                width: 'auto',
                height: '34px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                padding: '0 14px',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              <FiPlus size={16} strokeWidth={2.5} aria-hidden />
              Add Match
            </button>
          </div>
        )}
        <div
          style={{
            marginBottom: '14px',
            border: '1px solid #cbd5e1',
            borderRadius: '12px',
            overflow: 'hidden',
            background: '#eef2f6',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
          }}
        >
          <div style={{ padding: '14px 16px' }}>
            <div className="matches-filter-grid-all">
              <input
                type="text"
                name="associationId"
                value={filters.associationId}
                onChange={handleFilterChange}
                placeholder="Association ID"
                style={MATCHES_FILTER_INPUT_STYLE}
              />
              <input
                type="text"
                name="season"
                value={filters.season}
                onChange={handleFilterChange}
                placeholder="Season"
                style={MATCHES_FILTER_INPUT_STYLE}
              />
              <input
                type="text"
                name="tournament"
                value={filters.tournament}
                onChange={handleFilterChange}
                placeholder="Tournament"
                style={MATCHES_FILTER_INPUT_STYLE}
              />
              <input
                type="text"
                name="matchName"
                value={filters.matchName}
                onChange={handleFilterChange}
                placeholder="Match Name"
                style={MATCHES_FILTER_INPUT_STYLE}
              />
              <select
                name="matchType"
                value={filters.matchType}
                onChange={handleFilterChange}
                style={MATCHES_FILTER_SELECT_STYLE}
              >
                <option value="">Match Type</option>
                {matchTypeOptions.map((typeOption) => (
                  <option key={typeOption} value={typeOption}>
                    {formatMatchTypeLabel(typeOption)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="venue"
                value={filters.venue}
                onChange={handleFilterChange}
                placeholder="Venue"
                style={MATCHES_FILTER_INPUT_STYLE}
              />
              <input
                type="text"
                name="teamName"
                value={filters.teamName}
                onChange={handleFilterChange}
                placeholder="Team Name"
                style={MATCHES_FILTER_INPUT_STYLE}
              />
              <select
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                style={MATCHES_FILTER_SELECT_STYLE}
              >
                <option value="">Date Range</option>
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="custom">Custom Date Range</option>
              </select>
              {filters.startDate === 'custom' && (
                <>
                  <input
                    type="date"
                    name="customStartDate"
                    value={filters.customStartDate}
                    onChange={handleFilterChange}
                    style={MATCHES_FILTER_INPUT_STYLE}
                  />
                  <input
                    type="date"
                    name="customEndDate"
                    value={filters.customEndDate}
                    min={filters.customStartDate || undefined}
                    onChange={handleFilterChange}
                    style={MATCHES_FILTER_INPUT_STYLE}
                  />
                </>
              )}
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                style={MATCHES_FILTER_SELECT_STYLE}
              >
                <option value="">Feedback Status</option>
                <option value="pending">Feedback Pending</option>
                <option value="completed">Feedback Completed</option>
              </select>
              <button
                type="button"
                className="btn-action-outline"
                onClick={clearFilters}
                style={{
                  width: 'auto',
                  height: '34px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '0 14px',
                  color: 'rgb(197, 48, 48)',
                  border: 'none',
                  background: 'rgb(254, 215, 215)',
                }}
              >
                <FiX size={15} strokeWidth={2.25} aria-hidden style={{ flexShrink: 0 }} />
                Reset Filters
              </button>
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: '12px',
            border: '1px solid #eaecf0',
            borderRadius: '10px',
            boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              opacity: tableRefreshing ? 0.88 : 1,
              transition: 'opacity 0.2s ease-out',
              pointerEvents: tableRefreshing ? 'none' : 'auto',
            }}
          >
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', whiteSpace: 'nowrap' }}>
            <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>
              <tr>
                <th
                  scope="col"
                  style={{
                    textAlign: 'center',
                    padding: '10px 10px',
                    color: '#f8fafc',
                    fontSize: '12px',
                    width: '44px',
                  }}
                >
                  S.No
                </th>
                {renderSortableHeader('association_id', 'Association ID', 'Sort by Association ID')}
                {renderSortableHeader('season', 'Season', 'Sort by Season', { minWidth: '92px' })}
                {renderSortableHeader('tournament_name', 'Tournament', 'Sort by Tournament', {
                  minWidth: '130px',
                })}
                {renderSortableHeader('matchName', 'Match Name', 'Sort by Match Name')}
                {renderSortableHeader('matchType', 'Match Type', 'Sort by Match Type')}
                {renderSortableHeader('venue', 'Venue', 'Sort by Venue')}
                {renderSortableHeader('teamName', 'Team Name', 'Sort by Team Name')}
                {renderSortableHeader('startDate', 'Start Date and Time', 'Sort by Start Date', {
                  width: '140px',
                })}
                {renderSortableHeader('endDate', 'End Date and Time', 'Sort by End Date', {
                  width: '140px',
                })}
                <th
                  scope="col"
                  style={{
                    textAlign: 'center',
                    padding: '8px 10px',
                    color: '#f8fafc',
                    fontSize: '12px',
                    width: '112px',
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={MATCH_TABLE_COLUMN_COUNT} style={{ padding: '14px', textAlign: 'center', color: '#98a2b3', fontSize: '13px' }}>
                    Loading matches...
                  </td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={MATCH_TABLE_COLUMN_COUNT} style={{ padding: '14px', textAlign: 'center', color: '#98a2b3', fontSize: '13px' }}>
                    No matches found.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, index) => {
                  const matchNotStarted = isBeforeScheduledMatchStart({
                    startDate: row.date,
                    startTime: row.startTime,
                  });
                  const canEditMatch = canEditMatchPerm;
                  return (
                  <tr
                    key={row.id}
                    style={{
                      borderTop: '1px solid #eaecf0',
                      background: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                    }}
                  >
                    <td style={{ padding: '7px 10px', textAlign: 'center', fontSize: '12px', color: '#344054' }}>
                      {(safePage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '12px', color: '#344054' }}>
                      {row.associationId || '-'}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '12px', color: '#344054', minWidth: '92px' }}>
                      {row.season || '-'}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '12px', color: '#344054', minWidth: '130px' }}>
                      {row.tournamentName || '-'}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '12px', color: '#344054', fontWeight: 400 }}>
                      {row.matchName}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '12px', color: '#344054' }}>
                      {formatMatchTypeLabel(row.matchType)}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '12px', color: '#344054' }}>
                      {row.matchLocation || '-'}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '12px', color: '#344054' }}>
                      {canEditMatchPerm ? (
                        <div
                          title={
                            row.teamChangeLocked
                              ? 'Cannot change team after feedback has been submitted'
                              : ''
                          }
                          style={{ minWidth: '90px' }}
                        >
                          <StyledSelectDropdown
                            value={row.eventTeamId || ''}
                            onChange={(nextValue) => handleAssignTeam(row, nextValue)}
                            placeholder="Unassigned"
                            options={teamEventRows.map((t) => ({
                              value: String(t.id),
                              label: t.teamName || t.matchName || String(t.id),
                            }))}
                            disabled={row.teamChangeLocked}
                            triggerBorderColor="#d0d5dd"
                            triggerHeight={30}
                            maxListHeight={260}
                            ariaLabel={`Assign team for ${row.matchName || 'match'}`}
                          />
                        </div>
                      ) : (
                        row.teamName || '-'
                      )}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'center', fontSize: '12px', color: '#344054', whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.date, row.startTime)}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'center', fontSize: '12px', color: '#344054', whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.endDate, row.endTime)}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                        {/* View Match
                        <span className="icon-tooltip-wrap">
                          <button
                            type="button"
                            onClick={() => setSelectedMatch(row)}
                            style={{
                              border: canEditMatchPerm && matchNotStarted ? '1px solid #93c5fd' : '1px solid #d0d5dd',
                              background: canEditMatchPerm && matchNotStarted ? '#ffffff' : '#f2f4f7',
                              color: canEditMatchPerm && matchNotStarted ? '#2563eb' : '#98a2b3',
                              borderRadius: '6px',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            <FiEye />
                          </button>
                          <span className="icon-tooltip-bubble">View Match</span>
                        </span>
                        */}
                        {canEditMatchPerm && (
                          <span className="icon-tooltip-wrap">
                            <button
                              type="button"
                              onClick={() => openEditMatchModal(row)}
                              disabled={!canEditMatch}
                              title={
                                !matchNotStarted
                                  ? 'Match started. Only start/end date and time can be edited'
                                  : 'Edit Match'
                              }
                              style={{
                                border: canEditMatch ? '1px solid #93c5fd' : '1px solid #d0d5dd',
                                background: canEditMatch ? '#ffffff' : '#eaecf0',
                                color: canEditMatch ? '#2563eb' : '#667085',
                                borderRadius: '6px',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: canEditMatch ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                opacity: 1,
                                boxShadow: 'none',
                                outline: 'none',
                              }}
                            >
                              <FiEdit2 />
                            </button>
                            <span className="icon-tooltip-bubble">Edit Match</span>
                          </span>
                        )}
                        {canViewMatches && (
                        <span className="icon-tooltip-wrap">
                          <button
                            type="button"
                            onClick={() => setSelectedAssignedTeam(row)}
                            style={{
                              border: '1px solid #c4b5fd',
                              background: '#ffffff',
                              color: '#7c3aed',
                              borderRadius: '6px',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            <FiUsers />
                          </button>
                          <span className="icon-tooltip-bubble">View Team</span>
                        </span>
                        )}
                        {(() => {
                          const feedbackStatus = row.matchFeedbackStatus || (row.matchFeedbackSubmitted ? 'completed' : 'none');
                          const isCompleted = feedbackStatus === 'completed';
                          const isPartial = feedbackStatus === 'partial';
                          const canActAsTlForRow = canGiveTlFeedbackForMatchRow(row);
                          const showTlFeedbackButton =
                            canViewMatchTlFeedback || canGiveMatchTlFeedback;
                          if (!showTlFeedbackButton) return null;

                          const feedbackButtonStyle = isCompleted
                            ? { border: '1px solid #6ee7b7', background: '#ffffff', color: '#059669' }
                            : isPartial
                            ? { border: '1px solid #fcd34d', background: '#ffffff', color: '#d97706' }
                            : { border: '1px solid #fca5a5', background: '#ffffff', color: '#dc2626' };

                          const canGiveTlForRow =
                            !isCompleted && canGiveMatchTlFeedback && canActAsTlForRow;
                          const canViewTlNow =
                            (isCompleted || isPartial) && canViewMatchTlFeedback;
                          const canUseTlControl =
                            !matchNotStarted && (canViewTlNow || canGiveTlForRow);

                          return (
                            <span className="icon-tooltip-wrap">
                              <button
                                type="button"
                                disabled={!canUseTlControl}
                                title={
                                  matchNotStarted
                                    ? 'Match has not started yet'
                                    : !canUseTlControl &&
                                        canViewMatchTlFeedback &&
                                        !isCompleted &&
                                        !isPartial
                                    ? 'No TL feedback to view yet'
                                    : !isCompleted && canGiveMatchTlFeedback && !canActAsTlForRow
                                    ? 'Only team leader can give TL feedback'
                                    : undefined
                                }
                                onClick={() => {
                                  if (!canUseTlControl) return;
                                  if (isCompleted || (isPartial && canViewMatchTlFeedback && !canGiveTlForRow)) {
                                    openViewFeedback(row);
                                  } else {
                                    openFeedbackPage(row);
                                  }
                                }}
                                style={{
                                  ...(!canUseTlControl ? FEEDBACK_BTN_NOT_STARTED_STYLE : feedbackButtonStyle),
                                  borderRadius: '6px',
                                  width: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: !canUseTlControl ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                }}
                              >
                                {isCompleted || (isPartial && canViewMatchTlFeedback && !canGiveTlForRow) ? (
                                  <FiStar />
                                ) : (
                                  <FiMessageSquare />
                                )}
                              </button>
                              <span className="icon-tooltip-bubble">
                                {matchNotStarted
                                  ? 'Match not started'
                                  : !canUseTlControl &&
                                      canViewMatchTlFeedback &&
                                      !isCompleted &&
                                      !isPartial
                                  ? 'Nothing to view yet'
                                  : !canActAsTlForRow && !isCompleted && canGiveMatchTlFeedback
                                  ? 'TL only'
                                  : 'TL Feedback'}
                              </span>
                            </span>
                          );
                        })()}
                        {(() => {
                          const supOpensView = Boolean(row.supervisorFeedbackSubmitted);
                          const showSupervisorFeedbackButton =
                            (canGiveSupervisorFeedback || canViewSupervisorFeedback) &&
                            (matchNotStarted ||
                              (supOpensView ? canViewSupervisorFeedback : canGiveSupervisorFeedback));
                          if (!showSupervisorFeedbackButton) return null;

                          const canUseSupervisorControl =
                            !matchNotStarted &&
                            (supOpensView ? canViewSupervisorFeedback : canGiveSupervisorFeedback);

                          return (
                            <span className="icon-tooltip-wrap">
                              <button
                                type="button"
                                disabled={!canUseSupervisorControl}
                                title={matchNotStarted ? 'Match has not started yet' : undefined}
                                onClick={() => {
                                  if (matchNotStarted) return;
                                  handleSupervisorFeedbackClick(row);
                                }}
                                style={{
                                  ...(matchNotStarted
                                    ? FEEDBACK_BTN_NOT_STARTED_STYLE
                                    : (() => {
                                        const sup =
                                          row.supervisorFeedbackStatus ||
                                          (row.supervisorFeedbackSubmitted ? 'completed' : 'none');
                                        return {
                                          border:
                                            sup === 'completed'
                                              ? '1px solid #6ee7b7'
                                              : sup === 'partial'
                                              ? '1px solid #fcd34d'
                                              : '1px solid #fca5a5',
                                          background: '#ffffff',
                                          color:
                                            sup === 'completed'
                                              ? '#059669'
                                              : sup === 'partial'
                                              ? '#d97706'
                                              : '#dc2626',
                                          cursor: 'pointer',
                                        };
                                      })()),
                                  borderRadius: '6px',
                                  width: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                }}
                              >
                                <FiClipboard />
                              </button>
                              <span className="icon-tooltip-bubble">
                                {matchNotStarted ? 'Match not started' : 'Supervisor Feedback'}
                              </span>
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>

        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          totalItems={totalMatches}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      </div>

      {selectedMatch && (
        <div
          onClick={() => setSelectedMatch(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.62)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: '#ffffff',
              width: '100%',
              maxWidth: '460px',
              borderRadius: '14px',
              boxShadow: '0 20px 44px rgba(2, 6, 23, 0.2)',
              border: '1px solid #e8ecf0',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '12px 14px 11px',
                borderBottom: '1px solid #eef1f4',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div
                  aria-hidden
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: MATCH_MODAL_HEADER_ICON_CIRCLE_BG,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: MATCH_MODAL_ACCENT,
                  }}
                >
                  <FiAward size={18} strokeWidth={2} />
                </div>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '16px', fontWeight: 700, letterSpacing: '-0.02em' }}>
                  Match Details
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMatch(null)}
                aria-label="Close"
                style={{
                  border: '1px solid #e8ecf0',
                  background: '#f9fafb',
                  color: '#64748b',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <FiX size={17} strokeWidth={2} />
              </button>
            </div>

            <div style={{ padding: '12px 14px 14px' }}>
              <div
                style={{
                  display: 'grid',
                  gap: '8px',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                }}
              >
                {[
                  {
                    label: 'Association ID',
                    value: selectedMatch.associationId || '-',
                    Icon: FiCreditCard,
                  },
                  {
                    label: 'Season',
                    value: selectedMatch.season || '-',
                    Icon: FiCalendar,
                  },
                  {
                    label: 'Tournament Name',
                    value: selectedMatch.tournamentName || '-',
                    Icon: FiAward,
                  },
                  {
                    label: 'Match Name',
                    value: selectedMatch.matchName || '-',
                    Icon: FiTarget,
                  },
                  {
                    label: 'Location',
                    value: selectedMatch.matchLocation || '-',
                    Icon: FiMapPin,
                  },
                  {
                    label: 'Team Name',
                    value: selectedMatch.teamName || '-',
                    Icon: FiShield,
                  },
                  
                ].map(({ label, value, Icon }) => (
                  <div
                    key={label}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e8ecf0',
                      borderRadius: '10px',
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      aria-hidden
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: MATCH_MODAL_ICON_TILE_BG,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: MATCH_MODAL_ACCENT,
                      }}
                    >
                      <Icon size={16} strokeWidth={2} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: '10px',
                          color: '#64748b',
                          marginBottom: '3px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {label}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', lineHeight: 1.35, wordBreak: 'break-word' }}>
                        {value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: '10px',
                  background: MATCH_MODAL_SCHEDULE_PANEL_BG,
                  border: '1px solid rgba(102, 126, 234, 0.18)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '10px',
                    color: '#0f172a',
                    fontSize: '13px',
                    fontWeight: 700,
                  }}
                >
                  <FiCalendar size={16} strokeWidth={2} style={{ color: MATCH_MODAL_ACCENT }} aria-hidden />
                  Schedule
                </div>
                <div style={{ display: 'flex', alignItems: 'stretch', gap: '8px' }}>
                  {[
                    {
                      title: 'Start date and time',
                      text: formatDateTime(selectedMatch.date, selectedMatch.startTime),
                    },
                    {
                      title: 'End date and time',
                      text: formatDateTime(selectedMatch.endDate, selectedMatch.endTime),
                    },
                  ].map((slot, idx) => (
                    <React.Fragment key={slot.title}>
                      {idx === 1 && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            color: MATCH_MODAL_ACCENT,
                            alignSelf: 'center',
                          }}
                          aria-hidden
                        >
                          <FiChevronRight size={18} strokeWidth={2.5} />
                        </div>
                      )}
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: '#ffffff',
                          border: '1px solid #e8ecf0',
                          borderRadius: '10px',
                          padding: '10px 12px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '10px',
                            color: '#64748b',
                            marginBottom: '6px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                          }}
                        >
                          {slot.title}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiClock size={16} strokeWidth={2} style={{ color: MATCH_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', lineHeight: 1.35 }}>
                            {slot.text}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddMatchOpen && (
        <div
          onClick={closeAddMatchModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.54)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: '#ffffff',
              width: '100%',
              maxWidth: '560px',
              borderRadius: '12px',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.24)',
              padding: '16px',
            }}
          >
            <h3 style={{ margin: 0, color: '#101828' }}>
              {editingMatchId ? 'Update Match' : 'Add Match'}
            </h3>
            {editingMatchId && isTimeOnlyEdit && (
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#b54708' }}>
                Match has started. Only start/end date and time can be edited.
              </p>
            )}
            <form onSubmit={handleAddMatchSubmit} style={{ marginTop: '14px', display: 'grid', gap: '12px', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Association ID *
                </label>
                <input
                  type="text"
                  name="associationId"
                  value={addMatchForm.associationId}
                  onChange={handleAddMatchFieldChange}
                  disabled={isTimeOnlyEdit}
                  placeholder="Enter association id"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Season *
                </label>
                <input
                  type="text"
                  name="season"
                  value={addMatchForm.season}
                  onChange={handleAddMatchFieldChange}
                  disabled={isTimeOnlyEdit}
                  placeholder="Enter season"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Tournament Name *
                </label>
                <input
                  type="text"
                  name="tournamentName"
                  value={addMatchForm.tournamentName}
                  onChange={handleAddMatchFieldChange}
                  disabled={isTimeOnlyEdit}
                  placeholder="Enter tournament name"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Match Name *
                </label>
                <input
                  type="text"
                  name="matchName"
                  value={addMatchForm.matchName}
                  onChange={handleAddMatchFieldChange}
                  disabled={isTimeOnlyEdit}
                  placeholder="Enter match name"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Match Type *
                </label>
                <select
                  name="matchType"
                  value={addMatchForm.matchType}
                  onChange={handleAddMatchFieldChange}
                  disabled={isTimeOnlyEdit}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                >
                  <option value="">Select match type</option>
                  {matchTypeOptions.map((typeOption) => (
                    <option key={typeOption} value={typeOption}>
                      {typeOption}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Location *
                </label>
                <input
                  type="text"
                  name="matchLocation"
                  value={addMatchForm.matchLocation}
                  onChange={handleAddMatchFieldChange}
                  disabled={isTimeOnlyEdit}
                  placeholder="Enter match location"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={addMatchForm.startDate}
                  onChange={handleAddMatchFieldChange}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  Start Time *
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={addMatchForm.startTime}
                  onChange={handleAddMatchFieldChange}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={addMatchForm.endDate}
                  min={addMatchForm.startDate || undefined}
                  onChange={handleAddMatchFieldChange}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', marginBottom: '6px' }}>
                  End Time *
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={addMatchForm.endTime}
                  min={addMatchForm.endDate && addMatchForm.endDate === addMatchForm.startDate ? addMatchForm.startTime || undefined : undefined}
                  onChange={handleAddMatchFieldChange}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div></div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' }}>
                <button
                  type="button"
                  className="btn-action-outline"
                  onClick={closeAddMatchModal}
                  disabled={addMatchSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-action-primary" disabled={addMatchSubmitting}>
                  {!addMatchSubmitting && (
                    editingMatchId
                      ? <FiEdit2 size={16} strokeWidth={2.5} aria-hidden />
                      : <FiPlus size={16} strokeWidth={2.5} aria-hidden />
                  )}
                  {addMatchSubmitting ? 'Saving...' : editingMatchId ? 'Update Match' : 'Save Match'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedAssignedTeam && (
        <div
          onClick={() => setSelectedAssignedTeam(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.62)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: '#ffffff',
              width: '100%',
              maxWidth: '460px',
              borderRadius: '14px',
              boxShadow: '0 20px 44px rgba(2, 6, 23, 0.2)',
              border: '1px solid #e8ecf0',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '12px 14px 11px',
                borderBottom: '1px solid #eef1f4',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div
                  aria-hidden
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    background: TEAM_MEMBER_MODAL_HEADER_TILE_BG,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: TEAM_MEMBER_MODAL_ACCENT,
                  }}
                >
                  <FiUsers size={18} strokeWidth={2} />
                </div>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '16px', fontWeight: 700, letterSpacing: '-0.02em' }}>
                  Assigned Team
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAssignedTeam(null)}
                aria-label="Close"
                style={{
                  border: '1px solid #e8ecf0',
                  background: '#f9fafb',
                  color: '#64748b',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <FiX size={17} strokeWidth={2} />
              </button>
            </div>

            <div style={{ padding: '12px 14px 14px' }}>
              <div style={{ display: 'grid', gap: '8px', gridTemplateColumns: '1fr 1fr' }}>
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e8ecf0',
                    borderRadius: '10px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: TEAM_MEMBER_MODAL_ICON_TILE_BG,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: TEAM_MEMBER_MODAL_ACCENT,
                    }}
                  >
                    <FiUsers size={16} strokeWidth={2} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#64748b',
                        marginBottom: '3px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Team Name
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', lineHeight: 1.35, wordBreak: 'break-word' }}>
                      {selectedAssignedTeam.teamName || '-'}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e8ecf0',
                    borderRadius: '10px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: TEAM_MEMBER_MODAL_ICON_TILE_BG,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: TEAM_MEMBER_MODAL_ACCENT,
                    }}
                  >
                    <FiAward size={16} strokeWidth={2} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#64748b',
                        marginBottom: '3px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Team Leader
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', lineHeight: 1.35, wordBreak: 'break-word' }}>
                      {resolveAssignedTeamLeaderLabel(selectedAssignedTeam, usersById)}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: '10px',
                  background: '#ffffff',
                  border: '1px solid #e8ecf0',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead
                    style={{
                      background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px 10px',
                          fontSize: '11px',
                          color: '#f8fafc',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                          borderTopLeftRadius: '10px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <FiUsers size={13} strokeWidth={2} style={{ opacity: 0.95 }} aria-hidden />
                          Role
                        </span>
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px 10px',
                          fontSize: '11px',
                          color: '#f8fafc',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          borderTopRightRadius: '10px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <FiUsers size={13} strokeWidth={2} style={{ opacity: 0.95 }} aria-hidden />
                          Member
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedAssignedTeam.teamMembers || []).length === 0 ? (
                      <tr>
                        <td colSpan={2} style={{ padding: '10px 8px', textAlign: 'center', color: '#94a3b8', fontSize: '11px' }}>
                          No members assigned.
                        </td>
                      </tr>
                    ) : (
                      selectedAssignedTeam.teamMembers.map((member, index) => {
                        const { roleLabel, memberLabel } = resolveTeamMemberCells(
                          member,
                          usersById,
                          jobTypeLabelById
                        );
                        const RoleIcon = getRoleIconComponent(roleLabel);
                        const isLast = index === (selectedAssignedTeam.teamMembers || []).length - 1;
                        return (
                          <tr
                            key={`${member?.role ?? 'role'}-${member?.member ?? 'm'}-${index}`}
                            style={{ borderTop: '1px solid #eef1f4' }}
                          >
                            <td
                              style={{
                                padding: '8px 10px',
                                color: '#334155',
                                fontSize: '12px',
                                borderRight: '1px solid #eef1f4',
                                verticalAlign: 'middle',
                                borderBottomLeftRadius: isLast ? '10px' : undefined,
                              }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <RoleIcon size={15} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                                {roleLabel}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                color: '#334155',
                                fontSize: '12px',
                                verticalAlign: 'middle',
                                borderBottomRightRadius: isLast ? '10px' : undefined,
                              }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <FiUser size={15} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                                {memberLabel}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Matches;
